import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  Portal,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
  useColorModeValue,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { adminApi } from "../data/axios/adminApi";
import { OnflyLoading } from "../tools/OnflyLoading";

const maskCPF = (raw: string): string => {
  const d = raw.replace(/\D/g, "").slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9, 11)}`;
};

interface LeaderSearchInputProps {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  size?: string;
  bg?: string;
  isDisabled?: boolean;
}

export const LeaderSearchInput = ({
  value,
  onChange,
  placeholder = "Buscar gestor...",
  size = "sm",
  bg,
  isDisabled,
}: LeaderSearchInputProps) => {
  const [query, setQuery]     = useState(value ?? "");
  const [options, setOptions] = useState<{ label: string; email: string | null }[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen]       = useState(false);
  const containerRef          = useRef<HTMLDivElement>(null);
  const inputRef              = useRef<HTMLInputElement>(null);
  const dropdownRef           = useRef<HTMLDivElement>(null);
  const debounceRef           = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [dropdownRect, setDropdownRect] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 0 });

  // modal de cadastro
  const { isOpen: isModalOpen, onOpen: openModal, onClose: closeModal } = useDisclosure();
  const [newName, setNewName]   = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newCpf, setNewCpf]     = useState("");
  const [nameError, setNameError]   = useState("");
  const [emailError, setEmailError] = useState("");
  const [cpfError, setCpfError]     = useState("");
  const [isSaving, setIsSaving]   = useState(false);
  const toast = useToast();

  const dropdownBg     = useColorModeValue("white", "#1a2f4a");
  const dropdownBorder = useColorModeValue("rgba(0,0,0,0.12)", "rgba(255,255,255,0.1)");
  const itemHover      = useColorModeValue("rgba(0,158,251,0.07)", "rgba(0,158,251,0.12)");
  const itemColor      = useColorModeValue("#1a1a1a", "#e8edf2");
  const subtleColor    = useColorModeValue("#84858c", "#718096");
  const registerBg     = useColorModeValue("rgba(0,158,251,0.05)", "rgba(0,158,251,0.08)");
  const modalBg        = useColorModeValue("white", "#0f1f3a");
  const inputBg        = useColorModeValue("gray.50", "#0a1628");

  // Sincroniza o texto do input com o valor confirmado (value) quando o input
  // não está em foco — ex.: gestor já salvo ao abrir o drawer, ou após salvar.
  useEffect(() => {
    if (!open) setQuery(value ?? "");
  }, [value, open]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideContainer = containerRef.current?.contains(target);
      const insideDropdown  = dropdownRef.current?.contains(target);
      if (!insideContainer && !insideDropdown) {
        setOpen(false);
        setQuery(value ?? "");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [value]);

  const fetchOptions = (search: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await adminApi.listLeaders({ search: search || undefined });
        setOptions(data.leaders.map(l => ({ label: l.nome_lider ?? "", email: l.email_lider })));
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 300);
  };

  const handleFocus = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownRect({ top: rect.bottom + 2, left: rect.left, width: rect.width });
    }
    setOpen(true);
    if (!options.length) fetchOptions("");
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setQuery(v);
    onChange("");
    setOpen(true);
    fetchOptions(v);
  };

  const handleSelect = (label: string) => {
    setQuery(label);
    onChange(label);
    setOpen(false);
  };

  const handleOpenModal = () => {
    setOpen(false);
    setNewName(query.trim());
    setNewEmail("");
    setNewCpf("");
    setNameError("");
    setEmailError("");
    setCpfError("");
    openModal();
  };

  const handleCreate = async () => {
    const nome = newName.trim();
    let hasError = false;
    if (!nome) { setNameError("Nome obrigatório."); hasError = true; }
    if (!newEmail.trim()) { setEmailError("E-mail obrigatório."); hasError = true; }
    if (newCpf.replace(/\D/g, "").length !== 11) { setCpfError("CPF inválido."); hasError = true; }
    if (hasError) return;
    setIsSaving(true);
    try {
      const created = await adminApi.createLeader({
        nome_lider: nome,
        email_lider: newEmail.trim() || null,
        cpf_lider: newCpf.replace(/\D/g, "") || null,
      });
      const label = created.nome_lider ?? nome;
      setOptions(prev => [{ label, email: created.email_lider }, ...prev]);
      handleSelect(label);
      closeModal();
      toast({ title: "Gestor cadastrado com sucesso!", status: "success", duration: 3000, isClosable: true });
    } catch (err: any) {
      toast({
        title: "Erro ao cadastrar gestor",
        description: err?.response?.data?.detail ?? "Tente novamente.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Box ref={containerRef} position="relative" flex={1}>
        <Input
          ref={inputRef}
          size={size}
          placeholder={placeholder}
          value={query}
          onChange={handleChange}
          onFocus={handleFocus}
          bg={bg}
          borderRadius="md"
          autoComplete="off"
          isDisabled={isDisabled}
        />
        {open && (
          <Portal>
          <Box
            ref={dropdownRef}
            position="fixed"
            top={`${dropdownRect.top}px`}
            left={`${dropdownRect.left}px`}
            width={`${dropdownRect.width}px`}
            zIndex={9999}
            bg={dropdownBg}
            borderWidth="1px"
            borderColor={dropdownBorder}
            borderRadius="8px"
            boxShadow="0 4px 16px rgba(0,0,0,0.12)"
            maxH="220px"
            overflowY="auto"
          >
            {loading ? (
              <Box display="flex" justifyContent="center" py={3}>
                <OnflyLoading containerSize={48} />
              </Box>
            ) : options.length === 0 ? (
              <Box>
                <Box px={3} py={2.5}>
                  <Text fontSize="xs" color={subtleColor} fontStyle="italic">
                    {query.trim()
                      ? `Nenhum gestor encontrado para "${query}".`
                      : "Digite para buscar gestores."}
                  </Text>
                </Box>
                <Box
                  px={3}
                  py={2.5}
                  cursor="pointer"
                  bg={registerBg}
                  borderTop="1px solid"
                  borderColor={dropdownBorder}
                  _hover={{ bg: itemHover }}
                  onMouseDown={handleOpenModal}
                >
                  <Text fontSize="xs" fontWeight={600} color="blue.400">
                    + Cadastrar novo gestor{query.trim() ? ` "${query.trim()}"` : ""}
                  </Text>
                </Box>
              </Box>
            ) : (
              <>
                {options.map((o, i) => (
                  <Box
                    key={i}
                    px={3}
                    py={2}
                    cursor="pointer"
                    _hover={{ bg: itemHover }}
                    onMouseDown={() => handleSelect(o.label)}
                  >
                    <Text fontSize="sm" fontWeight={500} color={itemColor}>{o.label}</Text>
                    {o.email && (
                      <Text fontSize="xs" color={subtleColor}>{o.email}</Text>
                    )}
                  </Box>
                ))}
                <Box
                  px={3}
                  py={2}
                  cursor="pointer"
                  bg={registerBg}
                  borderTop="1px solid"
                  borderColor={dropdownBorder}
                  _hover={{ bg: itemHover }}
                  onMouseDown={handleOpenModal}
                >
                  <Text fontSize="xs" fontWeight={600} color="blue.400">
                    + Cadastrar novo gestor
                  </Text>
                </Box>
              </>
            )}
          </Box>
          </Portal>
        )}
      </Box>

      <Modal isOpen={isModalOpen} onClose={closeModal} isCentered size="sm">
        <ModalOverlay backdropFilter="blur(4px)" bg="blackAlpha.400" />
        <ModalContent borderRadius="2xl" bg={modalBg}>
          <ModalHeader fontSize="md" fontWeight={600} pb={1}>Cadastrar novo gestor</ModalHeader>
          <ModalCloseButton />
          <ModalBody pt={2} pb={4}>
            <VStack align="stretch" spacing={4}>
              <FormControl isInvalid={!!nameError} isRequired>
                <FormLabel fontSize="xs" fontWeight={600} color={subtleColor} textTransform="uppercase" letterSpacing="0.05em" mb={1.5}>
                  Nome completo
                </FormLabel>
                <Input
                  placeholder="Ex: Maria Silva"
                  value={newName}
                  onChange={e => { setNewName(e.target.value); if (nameError) setNameError(""); }}
                  isDisabled={isSaving}
                  bg={inputBg}
                  borderRadius="lg"
                  size="sm"
                  fontSize="sm"
                  _focus={{ borderColor: "blue.400", boxShadow: "0 0 0 1px var(--chakra-colors-blue-400)" }}
                />
                <FormErrorMessage fontSize="xs" mt={1}>{nameError}</FormErrorMessage>
              </FormControl>

              <FormControl isRequired isInvalid={!!emailError}>
                <FormLabel fontSize="xs" fontWeight={600} color={subtleColor} textTransform="uppercase" letterSpacing="0.05em" mb={1.5}>
                  E-mail
                </FormLabel>
                <Input
                  placeholder="gestor@empresa.com"
                  type="email"
                  value={newEmail}
                  onChange={e => { setNewEmail(e.target.value); if (emailError) setEmailError(""); }}
                  isDisabled={isSaving}
                  bg={inputBg}
                  borderRadius="lg"
                  size="sm"
                  fontSize="sm"
                  _focus={{ borderColor: "blue.400", boxShadow: "0 0 0 1px var(--chakra-colors-blue-400)" }}
                />
                <FormErrorMessage fontSize="xs" mt={1}>{emailError}</FormErrorMessage>
              </FormControl>

              <FormControl isRequired isInvalid={!!cpfError}>
                <FormLabel fontSize="xs" fontWeight={600} color={subtleColor} textTransform="uppercase" letterSpacing="0.05em" mb={1.5}>
                  CPF
                </FormLabel>
                <Input
                  placeholder="000.000.000-00"
                  value={newCpf}
                  onChange={e => { setNewCpf(maskCPF(e.target.value)); if (cpfError) setCpfError(""); }}
                  maxLength={14}
                  isDisabled={isSaving}
                  bg={inputBg}
                  borderRadius="lg"
                  size="sm"
                  fontSize="sm"
                  _focus={{ borderColor: "blue.400", boxShadow: "0 0 0 1px var(--chakra-colors-blue-400)" }}
                />
                <FormErrorMessage fontSize="xs" mt={1}>{cpfError}</FormErrorMessage>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter gap={2} pt={2}>
            <Button variant="ghost" size="sm" onClick={closeModal} isDisabled={isSaving}>Cancelar</Button>
            <Button colorScheme="blue" size="sm" onClick={handleCreate} isLoading={isSaving} borderRadius="lg">
              Cadastrar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
